import fuzzysort from "fuzzysort";
import { t } from "@/i18n-js/instance";
import { useAddSpaceModal } from "@/react/components/Modals/AddSpaceModal";
import { useIsLoadingAppData } from "@/react/hooks/useIsLoadingAppData";
import { usePunditUserContext } from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { sortSpaces } from "@circle-react/helpers/spaceHelpers";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import type { SpaceGroup } from "@circle-react/types/SpaceGroup";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Loader } from "@circle-react-uikit/Loader";
import { SpaceCard } from "./SpaceCard";

export interface ShowSpaceGroupProps {
  spaceGroup: SpaceGroup;
}

export const ShowSpaceGroup = ({ spaceGroup }: ShowSpaceGroupProps) => {
  const addSpaceModal = useAddSpaceModal();
  const { isLoading, currentCommunityMember } = usePunditUserContext();
  const isLoadingAppData = useIsLoadingAppData();
  const { sort, search } = useRouterQueryParams();
  let filteredSpaces = spaceGroup.spaces || [];
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  if (search && typeof search === "string") {
    filteredSpaces = fuzzysort
      .go(search, filteredSpaces, { keys: ["name"] })
      .map(res => res.obj);
  }

  if (sort && typeof sort === "string") {
    filteredSpaces = sortSpaces({ spaces: filteredSpaces, sort });
  }

  if (isLoading || isLoadingAppData) {
    return <Loader />;
  }

  if (filteredSpaces.length === 0) {
    if (!isAdmin) {
      return (
        <EmptyState
          header={t("space_groups.empty_group.title")}
          description={t("space_groups.empty_group.description")}
        />
      );
    }

    return (
      <EmptyState
        header={t("space_groups.empty_group.title")}
        description={t("space_groups.empty_group.description")}
        primaryLabel={t("space_groups.empty_group.cta")}
        onPrimaryClick={() =>
          addSpaceModal.show({ spaceGroupId: spaceGroup.id })
        }
      />
    );
  }

  return (
    <div className="react-page-space-show">
      <div className="space-group__spaces">
        {filteredSpaces.map(space => (
          <SpaceCard space={space} key={space.id} />
        ))}
      </div>
    </div>
  );
};
